/**
 * The support desk's response-time promises, as the screens read them.
 *
 * Mirrors apps/api/src/modules/settings/crm-sla.settings.ts (the numbers) and
 * apps/api/src/modules/crm-support/lib/sla.ts (what they mean).
 *
 * Note what is NOT here: the state machine. Whether a ticket is late is decided
 * once, on the server, and travels on the row - because the list filter has to
 * select the same set the badge draws, and a second implementation of "late" in
 * this file would be a second chance to disagree with it. What IS here is the
 * settings round trip and the two pure functions that turn a verdict into
 * something a person can read.
 */
import { api } from "./api";

export type SlaPriority = "low" | "normal" | "high" | "urgent";

/** Least urgent first - the order the settings panel draws its rows in. */
export const SLA_PRIORITIES: readonly SlaPriority[] = ["low", "normal", "high", "urgent"];

export type SlaState = "na" | "ok" | "due" | "breached" | "met";

export interface SlaVerdict {
  state: SlaState;
  dueAt: string | null;
  /** Minutes left, negative once the promise is past. Null when the state is `na`. */
  minutesLeft: number | null;
}

export interface TicketSla {
  firstResponse: SlaVerdict;
  resolve: SlaVerdict;
}

export interface SlaTargets {
  firstResponseMins: Record<SlaPriority, number | null>;
  resolveMins: Record<SlaPriority, number | null>;
}

export interface SlaSettingsPayload {
  targets: SlaTargets;
  /** The ladder the panel offers. Never applied on its own - see the API file. */
  suggested: SlaTargets;
}

export const getSlaTargets = () => api.get<SlaSettingsPayload>("/admin/crm/support/sla");

export const putSlaTargets = (targets: SlaTargets) =>
  api.put<SlaSettingsPayload>("/admin/crm/support/sla", targets);

export function emptySlaTargets(): SlaTargets {
  const blank = (): Record<SlaPriority, number | null> => ({
    low: null,
    normal: null,
    high: null,
    urgent: null,
  });
  return { firstResponseMins: blank(), resolveMins: blank() };
}

export function hasAnySlaTarget(targets: SlaTargets): boolean {
  return SLA_PRIORITIES.some(
    (p) => targets.firstResponseMins[p] !== null || targets.resolveMins[p] !== null,
  );
}

/**
 * Which of a ticket's two clocks the row should speak about.
 *
 * A ticket can be late on both, and a row has space for one badge. The rule is
 * "the worse state, and first response wins a tie", because first response is
 * the promise the customer is actually waiting on: a thread nobody has answered
 * is a different kind of failure from one being worked on slowly, and it is the
 * one somebody can fix in the next minute.
 *
 * Returns null when neither clock has anything to say, so a desk with no
 * promises - or a priority with none - draws no badge at all rather than a
 * reassuring grey one.
 */
export function worstVerdict(sla: TicketSla | undefined): { clock: "firstResponse" | "resolve"; verdict: SlaVerdict } | null {
  if (!sla) return null;
  const rank: Record<SlaState, number> = { na: 0, met: 1, ok: 2, due: 3, breached: 4 };
  const first = sla.firstResponse;
  const second = sla.resolve;
  const best = rank[first.state] >= rank[second.state]
    ? ({ clock: "firstResponse" as const, verdict: first })
    : ({ clock: "resolve" as const, verdict: second });
  return best.verdict.state === "na" ? null : best;
}

/**
 * Minutes as the shortest thing a person can read at a glance.
 *
 * Rounded DOWN to the coarser unit on purpose: "2 h" for anything from two hours
 * to two hours fifty-nine. A badge is read sideways while scanning a list, and
 * "2 h 47 min" is a number somebody has to stop and parse. The exact deadline is
 * on the detail page.
 *
 * The sign is dropped here - whether this is time left or time lost is what the
 * label around it says, and a minus sign inside a badge that already reads
 * "late" is noise.
 */
export function formatSlaGap(minutes: number): string {
  const m = Math.abs(Math.round(minutes));
  if (m < 60) return `${m} min`;
  const hours = Math.floor(m / 60);
  if (hours < 24) return `${hours} h`;
  return `${Math.floor(hours / 24)} p`;
}

/**
 * The tone a verdict paints with, in the CRM's own vocabulary.
 *
 * `met` is deliberately quiet rather than green. A promise kept is the normal
 * case and colouring every answered ticket would leave a list where the two
 * rows that need attention are the only ones NOT wearing a colour.
 */
export function slaTone(state: SlaState): "ok" | "wt" | "lt" | "mut" {
  if (state === "breached") return "lt";
  if (state === "due") return "wt";
  if (state === "ok") return "ok";
  return "mut";
}

/**
 * Minutes into the "2h 30" style the settings panel edits in, and back.
 *
 * The panel edits HOURS, because every promise a desk actually makes is a round
 * number of hours or days and asking for 480 minutes invites an arithmetic slip
 * that nothing downstream can catch. Fractions are allowed (0,5 h) so a
 * half-hour promise is still expressible.
 */
export function formatSlaHours(minutes: number | null): string {
  if (minutes === null) return "";
  const hours = minutes / 60;
  return Number.isInteger(hours) ? String(hours) : String(hours).replace(".", ",");
}

/**
 * A typed hour figure as whole minutes, or null.
 *
 * Same contract as the goal fields: empty is null, junk is null, and a partially
 * valid string does not become a partially valid promise. Rounded to the minute,
 * because that is the unit stored and a promise of 7,333 hours is a typo.
 */
export function parseSlaHours(input: string): number | null {
  const cleaned = input.replace(/[\s  ]/g, "").replace(",", ".");
  if (!cleaned) return null;
  if (!/^\d+(\.\d+)?$/.test(cleaned)) return null;
  const minutes = Math.round(Number(cleaned) * 60);
  if (!Number.isFinite(minutes) || minutes <= 0) return null;
  return minutes;
}
